import { extend, Browser, remove } from '@syncfusion/ej2-base';
import { PointData, ChartLocation } from '../../common/utils/helper';
import { getElement, measureText, Rect } from '@syncfusion/ej2-svg-base';
import { removeElement, withInBounds } from '../../common/utils/helper';
import { BaseTooltip } from '../../common/user-interaction/tooltip';
import { tooltipRender, sharedTooltipRender } from '../../common/model/constants';
import { isNullOrUndefined, initializeTelemetryFeature } from '@syncfusion/ej2-base';
/**
 * The `Tooltip` module is used to render tooltips for chart series.
 */
export class Tooltip extends BaseTooltip {
    /**
     * Constructor for the Touch module.
     *
     * @param {Chart} chart - The chart instance.
     */
    constructor(chart) {
        initializeTelemetryFeature('Tooltip', 'Charts');
        super(chart);
        this.commonXvalues = [];
        this.addEventListener();
    }
    /**
     * Adds event listeners for the chart.
     *
     * @returns {void}
     */
    addEventListener() {
        if (this.chart.isDestroyed) {
            return;
        }
        const cancelEvent = Browser.isPointer ? 'pointerleave' : 'mouseleave';
        this.chart.on(cancelEvent, this.mouseLeaveHandler, this);
        this.chart.on('tapHold', this.longPress, this);
        this.chart.on(Browser.touchMoveEvent, this.mouseMoveHandler, this);
        this.chart.on(Browser.touchEndEvent, this.mouseUpHandler, this);
    }
    mouseUpHandler() {
        const chart = this.control;
        const data = this.getData();
        data.lierIndex = this.lierIndex;
        if (chart.isTouch && !this.isSelected(chart) &&
            ((withInBounds(chart.mouseX, chart.mouseY, chart.chartAxisLayoutPanel.seriesClipRect)
                && (chart.tooltip.shared || chart.tooltip.split))
                || (!this.chart.tooltip.shared && !this.chart.tooltip.split))) {
            if (!chart.crosshair.enable) {
                this.tooltip();
                if (chart.tooltip.fadeOutMode === 'Move') {
                    this.removeTooltip(chart.tooltip.fadeOutDuration);
                }
            }
            else if (chart.startMove && chart.tooltip.fadeOutMode === 'Move') {
                this.removeTooltip(2000);
            }
        }
        else if (!this.findData(data, this.previousPoints[0]) && chart.tooltip.fadeOutMode === 'Click') {
            this.removeTooltip(0);
        }
    }
    mouseLeaveHandler() {
        this.removeTooltip(this.chart.tooltip.fadeOutDuration);
    }
    mouseMoveHandler() {
        const chart = this.chart;
        if (chart.stockChart && chart.stockChart.onPanning) {
            if (chart.mouseY < chart.chartAxisLayoutPanel.seriesClipRect.y) {
                chart.mouseY = chart.chartAxisLayoutPanel.seriesClipRect.y;
            }
            else if (chart.mouseY > chart.chartAxisLayoutPanel.seriesClipRect.y + chart.chartAxisLayoutPanel.seriesClipRect.height) {
                chart.mouseY = chart.chartAxisLayoutPanel.seriesClipRect.y + chart.chartAxisLayoutPanel.seriesClipRect.height;
            }
        }
        // Tooltip for chart series.
        if (!chart.disableTrackTooltip && !this.isSelected(chart)) {
            if ((!this.chart.tooltip.shared && !this.chart.tooltip.split) && (!chart.isTouch || (chart.startMove))) {
                this.tooltip();
            }
            if (withInBounds(chart.mouseX, chart.mouseY, chart.chartAxisLayoutPanel.seriesClipRect)) {
                if ((chart.tooltip.shared || chart.tooltip.split) && (!chart.isTouch || (chart.startMove))) {
                    this.tooltip();
                }
            }
            else {
                if ((chart.tooltip.shared || chart.tooltip.split) && chart.tooltip.fadeOutMode === 'Move') {
                    this.removeTooltip(this.chart.tooltip.fadeOutDuration);
                }
            }
        }
    }
    /**
     * Handles the long press on chart.
     *
     * @returns {boolean} false
     * @private
     */
    longPress() {
        const chart = this.chart;
        if (chart.crosshair.enable && withInBounds(chart.mouseX, chart.mouseY, chart.chartAxisLayoutPanel.seriesClipRect)) {
            this.tooltip();
            chart.markerRender.markerMove(false);
        }
        return false;
    }
    /**
     * Renders the tooltip.
     *
     * @returns {void}
     * @private
     */
    tooltip() {
        const elementId = this.chart.enableCanvas ? this.element.id + '_tooltip_group' : this.element.id + '_tooltip_svg';
        const svgElement = this.getElement(elementId);
        // To prevent the disappearance of the tooltip, while resize the stock chart.
        const isStockSvg = this.chart.stockChart && svgElement && (svgElement.firstChild.childNodes.length > 1);
        const isTooltip = (svgElement && parseInt(svgElement.getAttribute('opacity'), 10) > 0 && !isStockSvg);
        const tooltipDiv = this.getTooltipElement(isTooltip);
        const tooltipSvg = (document.getElementById(this.chart.element.id + '_tooltip_svg'));
        if (this.chart.enableCanvas && tooltipDiv && tooltipSvg) {
            document.getElementById(this.chart.element.id + '_Secondary_Element').appendChild(tooltipDiv);
            tooltipDiv.appendChild(tooltipSvg);
        }
        if (!this.chart.tooltip.shared && !this.chart.tooltip.split) {
            this.renderSeriesTooltip(this.chart, !isTooltip, tooltipDiv);
        }
        else {
            this.renderGroupedTooltip(this.chart, !isTooltip, tooltipDiv);
        }
    }
    findHeader(data) {
        if (this.header === '') {
            return '';
        }
        this.header = this.parseTemplate(data.point, data.series, this.header, data.series.xAxis, data.series.yAxis);
        if (this.header.replace(/<b>/g, '').replace(/<\/b>/g, '').trim() !== '') {
            return this.header;
        }
        return '';
    }
    findShapes() {
        if (!this.chart.tooltip.enableMarker) {
            return [];
        }
        const marker = [];
        for (const data of this.currentPoints) {
            marker.push(data.point.marker.shape || data.series.marker.shape || 'Circle');
        }
        return marker;
    }
    renderSeriesTooltip(chart, isFirst, tooltipDiv) {
        let data = this.getData();
        let closestPointData = null;
        let smallestDistance = Infinity;
        data.lierIndex = this.lierIndex;
        this.currentPoints = [];
        if (this.findData(data, this.previousPoints[0])) {
            if (!(chart.dataEditingModule && chart.dataEditingModule.isPointDragging) && (this.previousPoints[0] &&
                data.point.index === this.previousPoints[0].point.index
                && data.series.index === this.previousPoints[0].series.index && !(chart.tooltip.followPointer))) {
                return null;
            }
            if (this.pushData(data, isFirst, tooltipDiv, true)) {
                this.triggerTooltipRender(data, isFirst, this.getTooltipText(data), this.findHeader(data));
            }
        }
        else {
            const tooltipWithInBounds = withInBounds(chart.mouseX, chart.mouseY, chart.chartAxisLayoutPanel.seriesClipRect);
            if (!data.point && this.isRemove && chart.tooltip.fadeOutMode === 'Move' && (!chart.tooltip.showNearestTooltip || !tooltipWithInBounds)) {
                this.removeTooltip(this.chart.tooltip.fadeOutDuration);
                this.isRemove = false;
            }
            else {
                const commonXvalues = this.mergeXvalues(this.chart.visibleSeries);
                for (let i = chart.visibleSeries.length - 1; i >= 0; i--) {
                    const series = chart.visibleSeries[i];
                    if (series.visible && !(series.category === 'TrendLine')) {
                        data = this.getClosestX(chart, series, commonXvalues) || data;
                        if (chart.tooltip.showNearestTooltip && tooltipWithInBounds && series.showNearestTooltip && data &&
                            data.point && series.enableTooltip && data.point.symbolLocations[0]) {
                            const currentDistance = Math.sqrt(Math.pow((chart.mouseX - data.series.clipRect.x) -
                                data.point.symbolLocations[0].x, 2) + Math.pow((chart.mouseY -
                                data.series.clipRect.y) - data.point.symbolLocations[0].y, 2));
                            if (currentDistance < smallestDistance) {
                                smallestDistance = currentDistance;
                                closestPointData = data;
                            }
                        }
                    }
                }
                if (chart.tooltip.showNearestTooltip && closestPointData && this.pushData(closestPointData, isFirst, tooltipDiv, true)) {
                    data = closestPointData;
                    this.triggerTooltipRender(data, isFirst, this.getTooltipText(data), this.findHeader(data));
                }
            }
        }
        if (data && data.point) {
            this.findMouseValues(data, chart, this);
        }
    }
    triggerTooltipRender(point, isFirst, textCollection, headerText) {
        let tooltipTemplate;
        const argsData = {
            cancel: false, name: tooltipRender, text: textCollection, headerText: headerText, template: tooltipTemplate,
            series: point.series, textStyle: this.textStyle, point: point.point,
            data: { pointX: point.point.x, pointY: point.point.y, seriesIndex: point.series.index, seriesName: point.series.name,
                pointIndex: point.point.index, pointText: point.point.text }
        };
        const borderWidth = this.chart.border.width;
        const padding = 3;
        const tooltip = this.chart.tooltip;
        const chartTooltipSuccess = (argsData) => {
            if (!argsData.cancel) {
                if (point.series.type === 'BoxAndWhisker') {
                    this.removeText();
                    isFirst = true;
                }
                this.headerText = argsData.headerText;
                this.formattedText = this.formattedText.concat(argsData.text);
                this.text = this.formattedText;
                let location = this.getSymbolLocation(point);
                location = location ? location : new ChartLocation(null, null);
                this.applyTooltipDistance(location, point, this.chart);
                location.x = tooltip.location.x !== null ? tooltip.location.x : location.x;
                location.y = tooltip.location.y !== null ? tooltip.location.y : location.y;
                location = (location.x === null && location.y === null) ? null : location;
                this.createTooltip(this.chart, isFirst, location, point.series.clipRect, point.point, this.findShapes(), this.findMarkerHeight(this.currentPoints[0]), new Rect(borderWidth, borderWidth, this.chart.availableSize.width - padding - borderWidth * 2, this.chart.availableSize.height - padding - borderWidth * 2), this.chart.crosshair.enable, null, this.getTemplateText(point), this.template ? argsData.template : '');
            }
            else {
                this.removeHighlight();
                remove(this.getElement(this.element.id + '_tooltip'));
            }
            this.isRemove = true;
        };
        chartTooltipSuccess.bind(this, point);
        this.chart.trigger(tooltipRender, argsData, chartTooltipSuccess);
    }
    findMarkerHeight(pointData) {
        let markerHeight = 0;
        const series = pointData.series;
        markerHeight = ((series.marker.visible || ((this.chart.tooltip.shared || this.chart.tooltip.split) &&
            (!series.isRectSeries || series.marker.visible)) || series.type === 'Scatter' || series.drawType === 'Scatter')
            && series.type !== 'Bubble'
            && !(series.type === 'Candle' || series.type === 'Hilo' || series.type === 'HiloOpenClose')) ?
            ((series.marker.height + 2) / 2 + (2 * series.marker.border.width)) : 0;
        return markerHeight;
    }
    findData(data, previous) {
        return data.point && ((!previous || (previous.point !== data.point)) ||
            (previous && previous.lierIndex > 3 && previous.lierIndex !== this.lierIndex) || (previous.point === data.point));
    }
    getSymbolLocation(data) {
        let location;
        if (data.series.type !== 'BoxAndWhisker') {
            if (!data.point.symbolLocations[0]) {
                return null;
            }
            location = new ChartLocation(data.point.symbolLocations[0].x, data.point.symbolLocations[0].y);
        }
        switch (data.series.type) {
            case 'BoxAndWhisker':
                return this.getBoxLocation(data);
            case 'Waterfall':
                return this.getWaterfallRegion(data, location);
            case 'RangeArea':
            case 'RangeStepArea':
            case 'SplineRangeArea':
            case 'RangeColumn':
                return this.getRangeArea(data, location);
            default:
                return location;
        }
    }
    getRangeArea(data, location) {
        if (data.point.regions[0]) {
            if (!this.inverted) {
                location.y = data.point.regions[0].y + data.point.regions[0].height / 2;
            }
            else {
                location.x = data.point.regions[0].x + data.point.regions[0].width / 2;
            }
            if (data.series.type === 'RangeStepArea') {
                location.y = data.point.regions[0].y + data.point.regions[0].height / 2 + data.point.regions[0].width;
            }
        }
        return location;
    }
    getWaterfallRegion(data, location) {
        if (!this.inverted) {
            location.y = (data.point.y < 0) ?
                location.y - data.point.regions[0].height : location.y;
        }
        else {
            location.x = (data.point.y < 0) ?
                location.x + (this.chart.enableRtl ? -data.point.regions[0].width : data.point.regions[0].width) : location.x;
        }
        return location;
    }
    getTooltipText(pointData) {
        const series = pointData.series;
        return this.parseTemplate(pointData.point, series, this.getFormat(this.chart, series), series.xAxis, series.yAxis);
    }
    getTemplateText(data) {
        this.template = this.chart.enableHtmlSanitizer ? this.chart.sanitize(this.template) : this.template;
        if (this.template && (this.chart.tooltip.shared || this.chart.tooltip.split)) {
            const point = [];
            for (let i = 0; i < data.length; i++) {
                point[i] = extend({}, data[i].point);
                point[i].x = this.formatPointValue(data[i].point, data[i].series.xAxis, 'x', true, false);
                if ((data[i].series.seriesType === 'XY')) {
                    point[i].y = this.formatPointValue(data[i].point, data[i].series.yAxis, 'y', false, true);
                }
                else {
                    point[i].low = this.formatPointValue(data[i].point, data[i].series.yAxis, 'low', false, true);
                    point[i].high = this.formatPointValue(data[i].point, data[i].series.yAxis, 'high', false, true);
                }
            }
            return point;
        }
        else if (this.template) {
            const point = extend({}, data.point);
            point.x = this.formatPointValue(data.point, data.series.xAxis, 'x', true, false);
            if ((data.series.seriesType === 'XY')) {
                point.y = this.formatPointValue(data.point, data.series.yAxis, 'y', false, true);
            }
            else {
                point.low = this.formatPointValue(data.point, data.series.yAxis, 'low', false, true);
                point.high = this.formatPointValue(data.point, data.series.yAxis, 'high', false, true);
            }
            return point;
        }
        else {
            return data.point;
        }
    }
    renderGroupedTooltip(chart, isFirst, tooltipDiv) {
        let data;
        let dataCollection = [];
        let lastData;
        const pointData = chart.chartAreaType === 'PolarRadar' ? this.getData() : null;
        this.stopAnimation();
        this.removeHighlight();
        this.currentPoints = [];
        const extraPoints = [];
        let closestXValue = Number.MAX_VALUE;
        let closetYValue = Number.MAX_VALUE;
        let pointXValue;
        let pointYValue;
        let tempData;
        //let headerContent : string = '';
        if (isFirst) {
            if (!chart.stockChart) {
                if (tooltipDiv) {
                    document.getElementById(this.element.id + '_Secondary_Element').appendChild(tooltipDiv);
                }
            }
            else {
                if (tooltipDiv && !getElement(tooltipDiv.id)) {
                    document.getElementById(chart.stockChart.element.id + '_Secondary_Element').appendChild(tooltipDiv);
                }
            }
        }
        this.removeText();
        const argument = {
            text: [], cancel: false, name: sharedTooltipRender, data: [], point: [], series: [], headerText: '', textStyle: this.textStyle, template: []
        };
        let i = 0;
        for (const series of chart.visibleSeries) {
            if (!series.enableTooltip || !series.visible) {
                continue;
            }
            if (chart.chartAreaType === 'Cartesian' && series.visible) {
                data = this.getClosestX(chart, series, this.commonXValue(this.chart.visibleSeries));
            }
            else if (chart.chartAreaType === 'PolarRadar' && series.visible && pointData.point !== null) {
                data = new PointData(series.points[pointData.point.index], series);
            }
            // if (data && this.header !== '' && this.currentPoints.length === 0) {
            //     headerContent = this.findHeader(data);
            // }
            if (chart.tooltip.showNearestPoint && !data && !this.chart.tooltip.split) {
                data = this.getClosestX(chart, series, this.commonXValue([series]));
            }
            if (data) {
                argument.data.push({ pointX: data.point.x, pointY: data.point.y, seriesIndex: data.series.index,
                    seriesName: data.series.name, pointIndex: data.point.index, pointText: data.point.text });
                argument.series[i] = data.series;
                argument.point[i] = data.point;
                argument.headerText = this.findHeader(data);
                this.currentPoints.push(data);
                if (this.template != null) {
                    argument.template.push(chart.enableHtmlSanitizer ? chart.sanitize(this.template.toString()) : this.template.toString());
                }
                argument.text.push(this.getTooltipText(data));
                pointXValue = (!chart.requireInvertedAxis) ? chart.mouseX - data.series.clipRect.x : chart.mouseY - data.series.clipRect.y;
                pointYValue = chart.mouseY - data.series.clipRect.y;
                if (data.point.symbolLocations && data.point.symbolLocations.length &&
                    Math.abs(pointXValue - data.point.symbolLocations[0].x) <= closestXValue &&
                    Math.abs(data.point.symbolLocations[0].y - pointYValue) < Math.abs(closetYValue - pointYValue)) {
                    closestXValue = Math.abs(pointXValue - data.point.symbolLocations[0].x);
                    closetYValue = data.point.symbolLocations[0].y;
                    tempData = data;
                }
                lastData = tempData || data;
                dataCollection.push(data);
            }
            // if (data && this.triggerEvent(data, isFirst, this.getTooltipText(data)), this.findHeader(data)) {
            //     this.findMouseValue(data, chart);
            //     (<PointData[]>this.currentPoints).push(data);
            //     data = null;
            // } else if (data) {
            //     extraPoints.push(data);
            // }
            i++;
        }
        if (!chart.tooltip.showNearestPoint) {
            const collection = [];
            this.currentPoints = [];
            argument.point = [];
            argument.series = [];
            argument.data = [];
            argument.text = [];
            for (const data of dataCollection) {
                if (data.point.symbolLocations[0].x === lastData.point.symbolLocations[0].x || ((data.series.type.indexOf('Column') !== -1 ||
                    lastData.series.type.indexOf('Column') !== -1 ||
                    data.series.type.indexOf('Bar') !== -1 ||
                    lastData.series.type.indexOf('Bar') !== -1) && (data.point.xValue === lastData.point.xValue))) {
                    argument.point.push(data.point);
                    argument.series.push(data.series);
                    argument.text.push(this.getTooltipText(data));
                    argument.headerText = this.findHeader(data);
                    collection.push(data);
                    argument.data.push({
                        pointX: data.point.x, pointY: data.point.y, seriesIndex: data.series.index,
                        seriesName: data.series.name, pointIndex: data.point.index, pointText: data.point.text
                    });
                }
            }
            dataCollection = collection;
            this.currentPoints = collection;
        }
        if (dataCollection.length > 0 && this.currentPoints.length > 0) { // To avoid console error when we have empty chart with shared tooltip.
            this.triggerSharedTooltip(argument, lastData, extraPoints, chart, isFirst, dataCollection);
        }
        else if (this.getElement(this.element.id + '_tooltip_path')) {
            this.getElement(this.element.id + '_tooltip_path').setAttribute('d', '');
        }
    }
    /**
     * Applies the tooltip distance offset to the resolved tooltip location.
     *
     * @param {ChartLocation} location - The resolved tooltip location to be adjusted.
     * @param {PointData} point - The point data for which the tooltip is rendered.
     * @param {Chart} chart - The chart instance used to determine orientation and RTL.
     * @returns {void}
     * @private
     */
    applyTooltipDistance(location, point, chart) {
        const distance = Math.max(0, chart.tooltip.distance);
        if (!location || distance === 0) {
            return;
        }
        const series = point.series;
        if (!series.isRectSeries) {
            location.y -= distance;
            return;
        }
        const isNegative = point.point && typeof point.point.y === 'number' && point.point.y < 0;
        if (!chart.requireInvertedAxis) {
            location.y += isNegative ? distance : -distance;
        }
        else {
            const direction = chart.enableRtl ? -1 : 1;
            location.x += isNegative ? -distance * direction : distance * direction;
        }
    }
    triggerSharedTooltip(argument, point, extraPoints, chart, isFirst, dataCollection) {
        const tooltipTemplate = argument.template;
        const argsData = {
            cancel: false, name: sharedTooltipRender, text: argument.text, headerText: argument.headerText,
            textStyle: argument.textStyle, template: tooltipTemplate,
            point: argument.point, series: argument.series,
            data: argument.data
        };
        const borderWidth = this.chart.border.width;
        const padding = 3;
        let toolbarHeight;
        let titleHeight;
        const currentPoints = [];
        if (chart.stockChart) {
            toolbarHeight = chart.stockChart.enablePeriodSelector ? chart.stockChart.toolbarHeight : 0;
            titleHeight = measureText(this.chart.stockChart.title, this.chart.stockChart.titleStyle, this.chart.themeStyle.tooltipLabelFont).height + 10;
        }
        const sharedTooltip = (argsData) => {
            if (!argsData.cancel) {
                if (point.series.type === 'BoxAndWhisker') {
                    this.removeText();
                    isFirst = true;
                }
                for (let i = 0; i < argsData.text.length; i++) {
                    if (argsData.text[i]) {
                        currentPoints.push(this.currentPoints[i]);
                    }
                }
                this.currentPoints = currentPoints;
                this.formattedText = this.formattedText.concat(argsData.text);
                this.text = argsData.text;
                this.headerText = argsData.headerText;
                if (typeof (argsData.template) != 'object') {
                    argsData.template = argsData.template.split(',');
                    if (argsData.template.length > currentPoints.length) {
                        argsData.template = argsData.template.splice(argsData.template.length - 1);
                    }
                }
                else {
                    if (argsData.template.length > currentPoints.length) {
                        argsData.template.splice(argsData.template.length - 1);
                    }
                }
                const tooltip = this.chart.tooltip;
                const distance = this.chart.getModuleName() === 'chart'
                    ? Math.max(0, this.chart.tooltip.distance || 0)
                    : 0;
                this.findMouseValues(point, this.chart, this);
                let location = this.findSharedLocation();
                location = location ? location : new ChartLocation(null, null);
                let splitLocations;
                let splitClipBounds;
                let seriesTypes;
                if (tooltip.split) {
                    splitLocations = this.findSplitLocation();
                    [splitClipBounds, seriesTypes] = this.findSplitClipBounds();
                }
                // Apply distance  auto location
                if (distance > 0 &&
                    tooltip.location.x === null &&
                    tooltip.location.y === null) {
                    this.applyTooltipDistance(location, point, this.chart);
                }
                location.x = tooltip.location.x !== null ? tooltip.location.x : location.x;
                location.y = tooltip.location.y !== null ? tooltip.location.y : location.y;
                location = (location.x === null && location.y === null) ? null : location;
                if (point.series.type.indexOf('Range') === -1 || withInBounds(location.x, location.y, chart.chartAxisLayoutPanel.seriesClipRect)) {
                    this.createTooltip(chart, isFirst, location, this.currentPoints.length === 1 ? this.currentPoints[0].series.clipRect : null, dataCollection.length === 1 ? dataCollection[0].point : null, this.findShapes(), this.findMarkerHeight(this.currentPoints[0]), new Rect(borderWidth, (chart.stockChart ? (toolbarHeight + titleHeight + borderWidth) : borderWidth), this.chart.availableSize.width - padding - borderWidth * 2, this.chart.availableSize.height - padding - borderWidth * 2), this.chart.crosshair.enable, extraPoints, this.template ? this.getTemplateText(dataCollection) : null, this.template ? argsData.template.join('') : '', splitLocations, splitClipBounds, seriesTypes);
                }
                else {
                    removeElement(this.element.id + '_tooltip');
                    extraPoints.push(point);
                }
                point = null;
            }
            else {
                removeElement(this.element.id + '_tooltip');
                extraPoints.push(point);
            }
        };
        sharedTooltip.bind(this, point, extraPoints);
        this.chart.trigger(sharedTooltipRender, argsData, sharedTooltip);
    }
    findSharedLocation() {
        const stockChart = this.chart.stockChart;
        if (stockChart) {
            if (this.text.length === 1) {
                this.text.push('');
            }
            const toolbarHeight = stockChart.enablePeriodSelector ? stockChart.toolbarHeight : 0;
            const element = document.getElementById(stockChart.element.id + '_ChartTitle');
            const titleHeight = stockChart.title !== '' ? element.getBoundingClientRect().height + 10 : 0;
            if (stockChart.tooltip.position === 'Nearest') {
                return new ChartLocation(this.valueX, this.valueY + toolbarHeight + titleHeight);
            }
            return new ChartLocation(this.chart.chartAxisLayoutPanel.seriesClipRect.x + 5, this.chart.chartAxisLayoutPanel.seriesClipRect.y + toolbarHeight + 5 + titleHeight);
        }
        else {
            if (this.currentPoints.length > 1) {
                return new ChartLocation(this.valueX, this.valueY);
            }
            else {
                return this.getSymbolLocation(this.currentPoints[0]);
            }
        }
    }
    /**
     * Get symbol locations for the current points.
     * @returns {ChartLocation[]} Array of ChartLocation; invalid points are { x: null, y: null }.
     */
    findSplitLocation() {
        const locations = [];
        for (let index = 0; index < this.currentPoints.length; index++) {
            const symbolLocation = this.getSymbolLocation(this.currentPoints[index]);
            if (symbolLocation && symbolLocation.x != null && symbolLocation.y != null) {
                locations.push(symbolLocation);
            }
            else {
                locations.push({ x: null, y: null });
            }
        }
        return locations;
    }
    /**
     * Get clip bounds for each current point's series.
     * @returns {[Rect[], string[]]} clipBounds and seriesTypes arrays.
     * Missing series produce { x: null, y: null, width: null, height: null } and ''.
     */
    findSplitClipBounds() {
        const clipBounds = [];
        const seriesTypes = [];
        for (let i = 0; i < this.currentPoints.length; i++) {
            const point = this.currentPoints[i];
            if (point && point.series) {
                clipBounds.push(point.series.clipRect);
                seriesTypes.push(point.series.type);
            }
            else {
                clipBounds.push({ x: null, y: null, width: null, height: null });
                seriesTypes.push('');
            }
        }
        return [clipBounds, seriesTypes];
    }
    getBoxLocation(data) {
        const location = this.lierIndex > 3 ? (data.point.outliers.length > 0 ?
            data.point.symbolLocations[this.lierIndex - 4] : null) :
            {
                x: data.point.regions[0].x + (data.point.regions[0].width / 2),
                y: data.point.regions[0].y + (data.point.regions[0].height / 2)
            };
        return location;
    }
    /**
     * Parses the tooltip template string and resolves all supported point and series tokens.
     *
     * @param {Points} point - The chart point used to resolve point-based tooltip tokens.
     * @param {Series} series - The chart series used to resolve series-based tooltip tokens.
     * @param {string} format - The tooltip template format string.
     * @param {Axis} xAxis - The x-axis used to format x point values.
     * @param {Axis} yAxis - The y-axis used to format y point values.
     * @returns {string} - Returns the parsed tooltip text with resolved token values.
     * @private
     */
    parseTemplate(point, series, format, xAxis, yAxis) {
        const pointKeys = Object.keys(point);
        const seriesKeys = Object.keys(Object.getPrototypeOf(series));
        if (format.indexOf('${') !== -1) {
            let previous;
            do {
                previous = format;
                format = this.resolveTooltipTokens(format, point, series, pointKeys, seriesKeys, xAxis, yAxis);
            } while (format !== previous && format.indexOf('${') !== -1);
        }
        return format;
    }
    /**
     * Resolves tooltip tokens in the specified template string.
     *
     * @param {string} input - The tooltip template string that contains tokens to resolve.
     * @param {Points} point - The chart point used to resolve point-based tokens.
     * @param {Series} series - The chart series used to resolve series-based tokens.
     * @param {string[]} pointKeys - The available point property keys.
     * @param {string[]} seriesKeys - The available series property keys.
     * @param {Axis} xAxis - The x-axis used to format x point values.
     * @param {Axis} yAxis - The y-axis used to format y point values.
     * @returns {string} - Returns the tooltip template string with supported tokens resolved.
     * @private
     */
    resolveTooltipTokens(input, point, series, pointKeys, seriesKeys, xAxis, yAxis) {
        return input.replace(/\$\{([^{}]+)\}/g, this.replaceTooltipToken.bind(this, point, series, pointKeys, seriesKeys, xAxis, yAxis));
    }
    /**
     * Replaces a single tooltip token with the corresponding point or series value.
     *
     * @param {Points} point - The chart point used to resolve point-based tokens.
     * @param {Series} series - The chart series used to resolve series-based tokens.
     * @param {string[]} pointKeys - The available point property keys.
     * @param {string[]} seriesKeys - The available series property keys.
     * @param {Axis} xAxis - The x-axis used to format x point values.
     * @param {Axis} yAxis - The y-axis used to format y point values.
     * @param {string} match - The complete matched tooltip token text.
     * @param {string} token - The token content inside the tooltip expression.
     * @returns {string} - Returns the resolved tooltip token value, or the original token text when it cannot be resolved.
     * @private
     */
    replaceTooltipToken(point, series, pointKeys, seriesKeys, xAxis, yAxis, match, token) {
        const colonIndex = token.indexOf(':');
        let rawPath;
        let inlineFormat = '';
        if (colonIndex === -1) {
            rawPath = token;
        }
        else {
            rawPath = token.substring(0, colonIndex);
            inlineFormat = token.substring(colonIndex + 1).trim();
        }
        // Path should not contain leading, trailing, or internal whitespace.
        if (rawPath !== rawPath.trim() || /\s/.test(rawPath)) {
            return match;
        }
        const path = rawPath;
        let rawValue;
        let fallbackText = '';
        if (path.indexOf('point.') === 0) {
            const key = path.slice(6);
            if (pointKeys.indexOf(key) === -1) {
                return match;
            }
            rawValue = point[key];
            if (key === 'outliers' && !isNullOrUndefined(point[key])) {
                rawValue = point[key][this.lierIndex - 4];
            }
            if (isNullOrUndefined(rawValue)) {
                return key === 'size' ? this.getPointTooltipText(point, series, key, xAxis, yAxis) : String(rawValue);
            }
            if (typeof rawValue === 'number' && (isNaN(rawValue) || !isFinite(rawValue))) {
                return isNaN(rawValue) ? 'NaN' : rawValue < 0 ? '-Infinity' : 'Infinity';
            }
            fallbackText = this.getPointTooltipText(point, series, key, xAxis, yAxis);
        }
        else if (path.indexOf('series.') === 0) {
            const key = path.slice(7);
            if (seriesKeys.indexOf(key) === -1) {
                return match;
            }
            rawValue = series[key];
            if (isNullOrUndefined(rawValue)) {
                return String(rawValue);
            }
            fallbackText = String(rawValue);
        }
        else {
            return match;
        }
        // No inline format, so return the normal template value directly.
        if (colonIndex === -1 || inlineFormat === '') {
            return fallbackText;
        }
        return this.getFormattedTooltipValue(rawValue, inlineFormat, fallbackText, this.chart.intl, this.chart.useGroupingSeparator);
    }
    /**
     * Gets the formatted tooltip text for the specified point value.
     *
     * @param {Points} point - The chart point that contains the value to format.
     * @param {Series} series - The chart series associated with the point.
     * @param {string} key - The point property key to format.
     * @param {Axis} xAxis - The x-axis used to format x point values.
     * @param {Axis} yAxis - The y-axis used to format y point values.
     * @returns {string} - Returns the formatted tooltip text for the specified point value.
     * @private
     */
    getPointTooltipText(point, series, key, xAxis, yAxis) {
        const isXPoint = key === 'x' && series.category !== 'TrendLine';
        const isYPoint = key === 'high' || key === 'open' || key === 'close' ||
            key === 'low' || key === 'y' || key === 'minimum' ||
            key === 'maximum' || key === 'outliers' ||
            key === 'upperQuartile' || key === 'lowerQuartile' ||
            key === 'median';
        return this.formatPointValue(point, key === 'x' ? xAxis : yAxis, key, isXPoint, isYPoint);
    }
    formatPointValue(point, axis, dataValue, isXPoint, isYPoint) {
        let textValue;
        let customLabelFormat;
        let value;
        if (axis.valueType !== 'Category' && isXPoint) {
            customLabelFormat = axis.labelFormat && axis.labelFormat.match('{value}') !== null;
            const formattedValue = axis.valueType === 'Double' ? +point[dataValue] : point[dataValue];
            textValue = customLabelFormat ? axis.labelFormat.replace('{value}', axis.format(formattedValue)) :
                axis.format(formattedValue);
        }
        else if (isYPoint && !isNullOrUndefined(point[dataValue])) {
            customLabelFormat = axis.labelFormat && axis.labelFormat.match('{value}') !== null;
            value = dataValue === 'outliers' ? axis.format(point[dataValue][this.lierIndex - 4]) : axis.valueType === 'DateTime' ? axis.format(new Date(+point[dataValue])) :
                axis.format(+point[dataValue]);
            textValue = customLabelFormat ? axis.labelFormat.replace('{value}', value) : value;
        }
        else if (dataValue === 'size') {
            const format = this.chart.intl.getNumberFormat({ format: '', useGrouping: this.chart.useGroupingSeparator });
            textValue = typeof point[dataValue] === 'number' ? format(point[dataValue]) : point[dataValue];
            textValue = textValue ? textValue : '';
        }
        else {
            textValue = point[dataValue];
        }
        return textValue;
    }
    getFormat(chart, series) {
        if (series.tooltipFormat) {
            if (series.seriesType === 'XY' && series.category === 'Indicator') {
                return this.getIndicatorTooltipFormat(series, chart, chart.tooltip.format);
            }
            return series.tooltipFormat;
        }
        if (!series.tooltipFormat && chart.tooltip.format) {
            if (series.seriesType === 'XY' && series.category === 'Indicator') {
                return this.getIndicatorTooltipFormat(series, chart, chart.tooltip.format);
            }
            return chart.tooltip.format;
        }
        const textX = (series.type === 'Histogram') ? '${point.minimum}' + '-' + '${point.maximum}' : '${point.x}';
        const format = !chart.tooltip.shared && !chart.tooltip.split ? textX : '${series.name}';
        switch (series.seriesType) {
            case 'XY':
                if (series.category === 'Indicator') {
                    this.getIndicatorTooltipFormat(series, chart, chart.tooltip.format);
                }
                return format + ' : ' + ((series.type === 'Bubble') ? chart.theme.indexOf('Tailwind3') > -1 ? '${point.y}  Size : ${point.size}' : '<b>${point.y}</b>  Size : <b>${point.size}</b>' : chart.theme.indexOf('Tailwind3') > -1 ? '${point.y}' :
                    '<b>${point.y}</b>');
            case 'HighLow':
                return format + ('<br/>High : <b>${point.high}</b><br/>Low : <b>${point.low}</b>');
            case 'HighLowOpenClose':
                return format + ('<br/>High : <b>${point.high}</b><br/>Low : <b>${point.low}</b><br/>' +
                    'Open : <b>${point.open}</b><br/>Close : <b>${point.close}</b>');
            case 'BoxPlot': {
                return format + '<br/>' + (this.lierIndex > 3 ? 'Outliers : <b>${point.outliers}</b>' :
                    'Maximum : <b>${point.maximum}</b><br/>Q3 : <b>${point.upperQuartile}</b><br/>' +
                        'Median : <b>${point.median}</b><br/>Q1 : <b>${point.lowerQuartile}</b><br/>Minimum : <b>${point.minimum}</b>');
            }
            //default: return '';
        }
    }
    getIndicatorTooltipFormat(series, chart, format) {
        let toolTip;
        if (series.seriesType === 'XY') {
            toolTip = series.name + ' : <b>${point.y}</b>';
        }
        else {
            toolTip = format;
        }
        return toolTip;
    }
    /*
    * @hidden
    */
    removeHighlightedMarker(data, fadeOut) {
        for (const item of data) {
            removeElement(this.element.id + '_Series_' + item.series.index +
                '_Point_' + item.point.index + '_Trackball');
            if (this.chart.markerRender) {
                this.chart.markerRender.removeHighlightedMarker(item.series, item.point, fadeOut);
            }
        }
        this.previousPoints = [];
    }
    /**
     * Get module name.
     *
     * @returns {string} - Returns the module name.
     */
    getModuleName() {
        /**
         * Returns the module name
         */
        return 'Tooltip';
    }
    /**
     * To destroy the tooltip.
     *
     * @returns {void}
     * @private
     */
    destroy() {
        /**
         * Destroy method performed here
         */
    }
}
